import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { locationsAPI, coachesAPI, studentsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { Plus, Loader2, MapPin, Users, Phone, Edit, Trash2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const Locations = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [locations, setLocations] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [expandedLocation, setExpandedLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    capacity: 20,
    coach_ids: [],
    status: 'active',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [locationsRes, coachesRes, studentsRes] = await Promise.all([
        locationsAPI.getAll({}),
        coachesAPI.getAll({}),
        studentsAPI.getAll({}),
      ]);
      setLocations(locationsRes.data);
      setCoaches(coachesRes.data);
      setStudents(studentsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Please enter a location name');
      return;
    }

    setSubmitting(true);
    try {
      if (selectedLocation) {
        await locationsAPI.update(selectedLocation.id, formData);
        toast.success('Location updated successfully');
      } else {
        await locationsAPI.create(formData);
        toast.success('Location created successfully');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save location');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedLocation) return;
    setSubmitting(true);
    try {
      await locationsAPI.delete(selectedLocation.id);
      toast.success('Location deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedLocation(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete location');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      phone: '',
      capacity: 20,
      coach_ids: [],
      status: 'active',
    });
    setSelectedLocation(null);
  };

  const openEditDialog = (location) => {
    setSelectedLocation(location);
    setFormData({
      name: location.name,
      address: location.address || '',
      phone: location.phone || '',
      capacity: location.capacity,
      coach_ids: location.coach_ids || [],
      status: location.status,
    });
    setDialogOpen(true);
  };

  const getLocationStudents = (locationId) => {
    return students.filter(s => s.location_id === locationId);
  };

  const getLocationCoaches = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    if (!location) return [];
    return coaches.filter(c => location.coach_ids?.includes(c.id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Locations</h1>
          <p className="text-slate-500">Manage pool locations</p>
        </div>
        {isAdmin() && (
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="btn-primary" data-testid="add-location-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Location
          </Button>
        )}
      </div>

      {/* Locations List */}
      {locations.length === 0 ? (
        <div className="text-center py-12">
          <MapPin className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-400">No locations found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {locations.map((location) => (
            <Collapsible
              key={location.id}
              open={expandedLocation === location.id}
              onOpenChange={(open) => setExpandedLocation(open ? location.id : null)}
            >
              <Card className="overflow-hidden" data-testid={`location-card-${location.id}`}>
                <CollapsibleTrigger className="w-full">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center text-secondary">
                          <MapPin className="w-6 h-6" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-semibold text-slate-900">{location.name}</h3>
                          <div className="flex items-center gap-3 mt-1">
                            <Badge variant={location.status === 'active' ? 'default' : 'secondary'}>
                              {location.status}
                            </Badge>
                            <span className="text-sm text-slate-500 flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              {location.student_count} students
                            </span>
                            <span className="text-sm text-slate-500">
                              Capacity: {location.capacity}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isAdmin() && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { e.stopPropagation(); openEditDialog(location); }}
                              data-testid={`edit-location-${location.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { e.stopPropagation(); setSelectedLocation(location); setDeleteDialogOpen(true); }}
                              className="text-destructive"
                              data-testid={`delete-location-${location.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expandedLocation === location.id ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <div className="border-t px-6 py-4 bg-slate-50">
                    <div className="space-y-3 mb-4">
                      {location.address && (
                        <p className="text-sm text-slate-600 flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {location.address}
                        </p>
                      )}
                      {location.phone && (
                        <p className="text-sm text-slate-600 flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          {location.phone}
                        </p>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Responsible Coaches */}
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 mb-2">Responsible Coaches</h4>
                        <div className="space-y-2">
                          {getLocationCoaches(location.id).length === 0 ? (
                            <p className="text-sm text-slate-400">No coaches assigned</p>
                          ) : (
                            getLocationCoaches(location.id).map((coach) => (
                              <div key={coach.id} className="flex items-center gap-2 p-2 bg-white rounded-lg">
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-semibold">
                                  {coach.name.split(' ').map(n => n[0]).join('')}
                                </div>
                                <div>
                                  <span className="text-sm text-slate-700">{coach.name}</span>
                                  {coach.specialization && (
                                    <p className="text-xs text-slate-500">{coach.specialization}</p>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                      
                      {/* Students at Location */}
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 mb-2">Students at Location</h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {getLocationStudents(location.id).length === 0 ? (
                            <p className="text-sm text-slate-400">No students at this location</p>
                          ) : (
                            getLocationStudents(location.id).map((student) => (
                              <div
                                key={student.id}
                                className="flex items-center justify-between p-2 bg-white rounded-lg cursor-pointer hover:bg-slate-100"
                                onClick={() => navigate(`/students/${student.id}`)}
                              >
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center text-secondary text-xs font-semibold">
                                    {student.name.split(' ').map(n => n[0]).join('')}
                                  </div>
                                  <span className="text-sm text-slate-700">{student.name}</span>
                                </div>
                                <span className="text-xs text-slate-500">{student.program_name}</span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedLocation ? 'Edit Location' : 'Add New Location'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Location Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Mbabane Main Pool"
                data-testid="location-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Full address"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Phone number"
                />
              </div>
              <div className="space-y-2">
                <Label>Capacity</Label>
                <Input
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
                  min={1}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-location-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {selectedLocation ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Location</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedLocation?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Locations;
